import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  hireOrders, staffTransfers, dismissalOrders, plannedAccrualOrders, employees,
} from '@/api/hr';
import { vacationOrder } from '@/api/vacation';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Єдиний список усіх кадрових наказів (прийом / переміщення / звільнення /
 * зміна нарахувань) — субменю «Кадрові накази» розділу «Кадри і ЗП».
 *
 * Колонки: № (бейдж імпорту) / Дата / Вид наказа / Юрособа / ПIБ (масовий наказ
 * зміни нарахувань → «масовий») / Проведено. Фільтри зверху: ПIБ + Юрособа +
 * Вид + Проведено. Кнопка «+ Новий» — випадаючий вибір виду наказа.
 */

type Kind = 'hire' | 'transfer' | 'dismissal' | 'accrual' | 'vacation';

const KIND_LABEL: Record<Kind, string> = {
  hire: 'Прийом на роботу',
  transfer: 'Переміщення',
  dismissal: 'Звільнення',
  accrual: 'Зміна нарахувань',
  vacation: 'Відпустка',
};
const KIND_COLOR: Record<Kind, string> = {
  hire: '#16a34a', transfer: '#2563eb', dismissal: '#dc2626', accrual: '#9333ea',
  vacation: '#ca8a04',
};

interface Row {
  id: string;
  kind: Kind;
  document_number: string;
  document_date: string;
  organization_id: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source: string | null;
  pib: string;            // ПIБ або «масовий»
  route: string;          // куди вести по кліку
}

const fmtDate = (v: string) => (v ? new Date(v).toLocaleDateString('uk-UA') : '');

export default function HrOrdersPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | Kind>('');
  const [postedFilter, setPostedFilter] = useState<'' | 'posted' | 'draft'>('');
  const [showNew, setShowNew] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgParam = orgId || undefined;

  // 4 джерела наказів (фільтр по юрособі — на бекенді).
  const hireQ = useQuery({
    queryKey: ['hr-orders', 'hire', orgId],
    queryFn: () => hireOrders.list({ organization_id: orgParam, limit: 500 }),
  });
  const xferQ = useQuery({
    queryKey: ['hr-orders', 'transfer', orgId],
    queryFn: () => staffTransfers.list({ organization_id: orgParam, limit: 500 }),
  });
  const disQ = useQuery({
    queryKey: ['hr-orders', 'dismissal', orgId],
    queryFn: () => dismissalOrders.list({ organization_id: orgParam, limit: 500 }),
  });
  const accrQ = useQuery({
    queryKey: ['hr-orders', 'accrual', orgId],
    queryFn: () => plannedAccrualOrders.list({ organization_id: orgParam, limit: 500 }),
  });
  const vacQ = useQuery({
    queryKey: ['hr-orders', 'vacation', orgId],
    queryFn: () => vacationOrder.list({ organization_id: orgParam, limit: 500 }),
  });
  // ПIБ для hire/transfer/dismissal (у них один employee_id). Наказ зміни нарахувань
  // несе employee_name у items сам. Тягнемо співробітників обраної юрособи (або всіх).
  const empQ = useQuery({
    queryKey: ['hr-orders-emps', orgId],
    queryFn: () => employees.list({ organization_id: orgParam, limit: 2000 }),
  });

  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data],
  );
  const empName = useMemo(
    () => new Map((empQ.data?.items ?? []).map((e) => [e.id, e.full_name ?? e.code ?? '—'])),
    [empQ.data],
  );

  const loading = hireQ.isLoading || xferQ.isLoading || disQ.isLoading || accrQ.isLoading || vacQ.isLoading;

  const allRows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const d of hireQ.data?.items ?? []) out.push({
      id: d.id, kind: 'hire', document_number: d.document_number, document_date: d.document_date,
      organization_id: d.organization_id, posted: d.posted, deletion_mark: d.deletion_mark,
      external_source: d.external_source, pib: empName.get(d.employee_id) ?? '—',
      route: `/hire-orders/${d.id}`,
    });
    for (const d of xferQ.data?.items ?? []) out.push({
      id: d.id, kind: 'transfer', document_number: d.document_number, document_date: d.document_date,
      organization_id: d.organization_id, posted: d.posted, deletion_mark: d.deletion_mark,
      external_source: d.external_source, pib: empName.get(d.employee_id) ?? '—',
      route: `/staff-transfers/${d.id}`,
    });
    for (const d of disQ.data?.items ?? []) out.push({
      id: d.id, kind: 'dismissal', document_number: d.document_number, document_date: d.document_date,
      organization_id: d.organization_id, posted: d.posted, deletion_mark: d.deletion_mark,
      external_source: d.external_source, pib: empName.get(d.employee_id) ?? '—',
      route: `/dismissal-orders/${d.id}`,
    });
    for (const d of accrQ.data?.items ?? []) out.push({
      id: d.id, kind: 'accrual', document_number: d.document_number, document_date: d.document_date,
      organization_id: d.organization_id, posted: d.posted, deletion_mark: d.deletion_mark,
      external_source: d.external_source ?? null,
      // Один рядок → ПIБ; кілька → «масовий».
      pib: d.items.length === 1 ? (d.items[0]?.employee_name ?? '—') : `масовий (${d.items.length})`,
      route: `/payroll/planned-accrual-orders/${d.id}`,
    });
    for (const d of vacQ.data?.items ?? []) out.push({
      id: d.id, kind: 'vacation', document_number: d.document_number, document_date: d.document_date,
      organization_id: d.organization_id, posted: d.posted, deletion_mark: d.deletion_mark,
      external_source: d.external_source ?? null, pib: empName.get(d.employee_id) ?? '—',
      route: `/hr/vacation-orders/${d.id}`,
    });
    return out;
  }, [hireQ.data, xferQ.data, disQ.data, accrQ.data, vacQ.data, empName]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allRows.filter((r) => {
      if (kindFilter && r.kind !== kindFilter) return false;
      if (postedFilter === 'posted' && !r.posted) return false;
      if (postedFilter === 'draft' && r.posted) return false;
      if (needle && !r.pib.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [allRows, q, kindFilter, postedFilter]);

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'document_number', header: '№', width: 130, importBadge: true,
      sortValue: (r) => r.document_number ?? '',
      cell: (r) => <a style={{ cursor: 'pointer' }} onClick={() => navigate(r.route)}>{r.document_number}</a>,
    },
    {
      key: 'document_date', header: 'Дата', width: 110,
      sortValue: (r) => r.document_date ?? '', cell: (r) => fmtDate(r.document_date),
    },
    {
      key: 'kind', header: 'Вид наказа', width: 170,
      sortValue: (r) => KIND_LABEL[r.kind],
      cell: (r) => <span style={{ color: KIND_COLOR[r.kind], fontWeight: 600 }}>{KIND_LABEL[r.kind]}</span>,
    },
    {
      key: 'org', header: 'Юрособа', width: 190,
      sortValue: (r) => (orgName.get(r.organization_id) ?? '').toLowerCase(),
      cell: (r) => orgName.get(r.organization_id) ?? '—',
    },
    {
      key: 'pib', header: 'ПIБ', width: 220,
      sortValue: (r) => r.pib.toLowerCase(),
      cell: (r) => r.pib.startsWith('масовий')
        ? <span style={{ color: '#64748b' }}>{r.pib}</span> : r.pib,
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => (r.posted ? 1 : 0),
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [navigate, orgName]);

  const selStyle = { width: 150, flex: '0 0 auto' } as const;

  // «+ Новий»: прийом = створення працівника (наказ виникає разом з карткою);
  // решта — сторінки-редактори у режимі створення.
  const NEW_OPTIONS: Array<{ kind: Kind; label: string; to: string }> = [
    { kind: 'hire', label: 'Прийом на роботу', to: '/employees/new-unified' },
    { kind: 'transfer', label: 'Кадрове переміщення', to: '/staff-transfers/new' },
    { kind: 'dismissal', label: 'Звільнення', to: '/dismissal-orders/new' },
    { kind: 'vacation', label: 'Наказ відпустки', to: '/hr/vacation-orders/new' },
    { kind: 'accrual', label: 'Зміна нарахувань', to: '/payroll/planned-accrual-orders/new' },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>Кадрові накази</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selStyle}>
          <option value="">Усі юрособи</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
        </select>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as '' | Kind)} style={selStyle}>
          <option value="">Усі види</option>
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <select value={postedFilter} onChange={(e) => setPostedFilter(e.target.value as '' | 'posted' | 'draft')} style={selStyle}>
          <option value="">Усі статуси</option>
          <option value="posted">Проведені</option>
          <option value="draft">Чернетки</option>
        </select>
        <input placeholder="ПIБ…" value={q} onChange={(e) => setQ(e.target.value)}
               style={{ width: 160, flex: '0 0 auto' }} />

        {/* «+ Новий» з випадаючим вибором виду наказа. */}
        <div style={{ position: 'relative', marginLeft: 'auto' }}
             onMouseLeave={() => setShowNew(false)}>
          <button className="primary" onClick={() => setShowNew((s) => !s)}>+ Новий ▾</button>
          {showNew && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
              background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden',
            }}>
              {NEW_OPTIONS.map((o) => (
                <button key={o.kind} onClick={() => { setShowNew(false); navigate(o.to); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    background: 'none', padding: '0.5rem 0.75rem', cursor: 'pointer',
                    color: KIND_COLOR[o.kind], fontWeight: 600,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => `${r.kind}-${r.id}`}
        loading={loading}
        emptyText="Немає кадрових наказів."
        onRowDoubleClick={(r) => navigate(r.route)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
